import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useMatch } from 'react-router';
import { ThemeProvider } from './components/ThemeProvider';
import { AuthModal } from './components/AuthModal';
import { LandingPage } from './components/LandingPage';
import { SecuritySettings } from './components/SecuritySettings';
import { TABS, DEFAULT_TAB, tabFromLegacyHash } from './tabs';
import { CheckoutModal } from './components/CheckoutModal';
import { TopBar } from './components/TopBar';
import { Sidebar } from './components/Sidebar';
import { MobileNav } from './components/MobileNav';
import { OnboardingIntentModal } from './components/OnboardingIntentModal';
import { authClient } from './lib/auth';
import { useUsage } from './lib/useUsage';

const ShareView = lazy(() => import('./components/ShareView').then(m => ({ default: m.ShareView })));

function App() {
  const { data: session, isPending } = authClient.useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const shareMatch = useMatch('/share/:token');
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalConfig, setAuthModalConfig] = useState<{ isLogin: boolean; tierId?: string } | null>(null);
  const [checkoutPlan, setCheckoutPlan] = useState<{ id: string, name: string, price: string } | null>(null);
  const [showSecuritySettings, setShowSecuritySettings] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'error' | 'info' | 'success' } | null>(null);

  const { storagePercent, storageUsedStr, isWarning, isBlocked, planName, refresh: refreshUsage } = useUsage();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [onboardingIntent, setOnboardingIntent] = useState<string | null>(null);
  const [isCorporateDomain, setIsCorporateDomain] = useState<boolean>(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);

  useEffect(() => {
    if (!session) {
      setIsAdmin(false);
      setOnboardingIntent(null);
      setIsCorporateDomain(false);
      setShowOnboardingModal(false);
      return;
    }
    fetch('/api/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.isAdmin) setIsAdmin(true);
        if (data?.isCorporateDomain) setIsCorporateDomain(true);
        if (data?.onboardingIntent) {
          setOnboardingIntent(data.onboardingIntent);
        } else if (data && data.onboardingIntent === null) {
          setOnboardingIntent(null);
          setShowOnboardingModal(true);
        }
      })
      .catch(() => setIsAdmin(false));
  }, [session]);

  const handleSelectIntent = async (intent: 'family' | 'studio' | 'business') => {
    try {
      const res = await fetch('/api/me/onboarding-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setOnboardingIntent(intent);
        setShowOnboardingModal(false);
        window.dispatchEvent(
          new CustomEvent('app:toast', {
            detail: {
              message:
                intent === 'studio'
                  ? 'Welcome! Client Proofing and Guest Dropboxes are ready.'
                  : intent === 'business'
                  ? 'Welcome! Team Workspace and 4K media engine ready.'
                  : 'Welcome! Your secure family media vault is ready.',
              type: 'success',
            },
          })
        );
        // Quick reload ensures active organization context is refreshed everywhere
        setTimeout(() => {
          window.location.reload();
        }, 600);
      } else {
        console.error('Failed to set intent:', data);
        setShowOnboardingModal(false);
      }
    } catch (e) {
      console.error(e);
      setShowOnboardingModal(false);
    }
  };

  // Listen for global toasts
  useEffect(() => {
    const handleToast = (e: any) => {
      const detail = e.detail;
      if (detail?.message) {
        setToast(detail);
        setTimeout(() => setToast(null), 6000);
      }
    };
    window.addEventListener('app:toast', handleToast);
    return () => window.removeEventListener('app:toast', handleToast);
  }, []);

  // Support both /photos and /w/:workspaceId/photos as well as ?w=org...
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isWorkspacePath = pathParts[0] === 'w' && Boolean(pathParts[1]);
  const urlWorkspaceId = isWorkspacePath ? pathParts[1] : null;
  const activeTab = isWorkspacePath ? (pathParts[2] || DEFAULT_TAB) : (pathParts[0] || DEFAULT_TAB);

  const searchParams = new URLSearchParams(location.search);
  const targetWorkspaceId = searchParams.get('w') || urlWorkspaceId;

  const setActiveTab = (id: string) => {
    if (urlWorkspaceId) {
      navigate(`/w/${urlWorkspaceId}/${id}${location.search}`);
    } else if (targetWorkspaceId) {
      navigate(`/${id}?w=${targetWorkspaceId}`);
    } else {
      navigate(`/${id}`);
    }
  };

  // Migrate any legacy hash URL (e.g. /#photos -> /photos) in browser address bar
  useEffect(() => {
    if (window.location.hash) {
      const cleanTab = tabFromLegacyHash(window.location.hash, isAdmin);
      window.history.replaceState(null, '', `/${cleanTab}${location.search}`);
    }
  }, [isAdmin, location.search]);

  const [gridLayout, setGridLayout] = useState<'square' | 'justified'>(() => {
    return (localStorage.getItem('filesharez-grid-layout') as 'square' | 'justified') || 'square';
  });

  const handleSetGridLayout = (layout: 'square' | 'justified') => {
    setGridLayout(layout);
    localStorage.setItem('filesharez-grid-layout', layout);
  };

  const [searchQuery, setSearchQuery] = useState('');

  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: userOrgs } = authClient.useListOrganizations();

  // Auto-switch workspace if link targets a specific workspace ID (e.g. ?w=org... or /w/org...)
  useEffect(() => {
    if (!session || !targetWorkspaceId || !activeOrg) return;

    if (activeOrg.id === targetWorkspaceId) {
      return;
    }

    let cancelled = false;
    const switchOrg = async () => {
      try {
        const res = await authClient.organization.setActive({ organizationId: targetWorkspaceId });
        if (cancelled) return;
        if (res?.error) {
          throw new Error(res.error.message || 'Failed to switch workspace');
        }

        const orgMatch = userOrgs?.find((o) => o.id === targetWorkspaceId);
        const orgName = orgMatch?.name || 'Workspace';

        window.dispatchEvent(
          new CustomEvent('app:toast', {
            detail: {
              message: `Switched to workspace: ${orgName}`,
              type: 'success',
            },
          })
        );
        window.dispatchEvent(new CustomEvent('usage:refresh'));
      } catch (err: any) {
        if (cancelled) return;
        window.dispatchEvent(
          new CustomEvent('app:toast', {
            detail: {
              message: `You do not have access to workspace: ${targetWorkspaceId}`,
              type: 'error',
            },
          })
        );
      }
    };

    switchOrg();
    return () => {
      cancelled = true;
    };
  }, [session, targetWorkspaceId, activeOrg?.id, userOrgs]);

  // Handle invitation link (?invite=inv...)
  const inviteToken = searchParams.get('invite');
  useEffect(() => {
    if (!inviteToken) return;

    if (session) {
      let isCancelled = false;
      const accept = async () => {
        try {
          const res = await authClient.organization.acceptInvitation({ invitationId: inviteToken });
          if (isCancelled) return;
          if (res?.error) {
            throw new Error(res.error.message || 'Invitation is invalid, expired, or already accepted');
          }
          window.dispatchEvent(
            new CustomEvent('app:toast', {
              detail: {
                message: '🎉 Successfully joined the workspace! Welcome aboard.',
                type: 'success',
              },
            })
          );
          // Clean the invite parameter from the URL
          const newParams = new URLSearchParams(location.search);
          newParams.delete('invite');
          const cleanQuery = newParams.toString() ? `?${newParams.toString()}` : '';
          navigate(`${location.pathname}${cleanQuery}`, { replace: true });
          window.location.reload();
        } catch (err: any) {
          if (isCancelled) return;
          window.dispatchEvent(
            new CustomEvent('app:toast', {
              detail: {
                message: err.message || 'Could not accept invitation.',
                type: 'error',
              },
            })
          );
        }
      };
      accept();
      return () => {
        isCancelled = true;
      };
    } else {
      // User is not logged in: persist invite token so it auto-accepts right after login/signup
      sessionStorage.setItem('pending_invite_token', inviteToken);
      setShowAuthModal(true);
      window.dispatchEvent(
        new CustomEvent('app:toast', {
          detail: {
            message: 'You have been invited to a workspace! Please sign in or create an account to accept.',
            type: 'info',
          },
        })
      );
    }
  }, [inviteToken, session]);

  // Check for pending invite after login/signup
  useEffect(() => {
    if (!session) return;
    const pendingInvite = sessionStorage.getItem('pending_invite_token');
    if (pendingInvite) {
      sessionStorage.removeItem('pending_invite_token');
      authClient.organization.acceptInvitation({ invitationId: pendingInvite })
        .then((res: any) => {
          if (!res?.error) {
            window.dispatchEvent(
              new CustomEvent('app:toast', {
                detail: {
                  message: '🎉 Successfully joined the workspace!',
                  type: 'success',
                },
              })
            );
            window.location.reload();
          }
        })
        .catch(() => {});
    }
  }, [session]);

  const tenantId = activeOrg?.id || '';

  if (shareMatch) {
    return (
      <ThemeProvider>
        <Suspense fallback={null}>
          <ShareView token={shareMatch.params.token ?? ''} />
        </Suspense>
      </ThemeProvider>
    );
  }

  if (isPending) {
    return (
      <ThemeProvider>
        <div style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: '1.5rem', color: 'var(--text-secondary)' }}>Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  if (!session) {
    return (
      <ThemeProvider>
        <LandingPage 
          onSignInClick={() => {
            setAuthModalConfig({ isLogin: true });
            setShowAuthModal(true);
          }} 
          onSelectPlan={(tierId) => {
            setAuthModalConfig({ isLogin: false, tierId });
            setShowAuthModal(true);
          }}
        />
        {showAuthModal && (
          <AuthModal 
            initialIsLogin={authModalConfig?.isLogin}
            initialTierId={authModalConfig?.tierId}
            onProceedToCheckout={(tierId) => {
              setCheckoutPlan({ id: tierId, name: 'Premium Plan', price: '$$$' });
            }}
            onClose={() => setShowAuthModal(false)} 
            onSuccess={() => setShowAuthModal(false)}
          />
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="app-layout">
        
        {/* Desktop Sidebar */}
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          storageUsedStr={storageUsedStr}
          storagePercent={storagePercent}
          planName={planName}
          onUpgradeClick={() => setCheckoutPlan({ id: 'personal', name: 'Personal', price: '$4.90' })}
        />

        {/* Main Content Area */}
        <div className="app-main">
          
          <TopBar 
            userEmail={session.user.email} 
            onSearch={(q) => { setSearchQuery(q); if(q) setActiveTab('explore'); else setActiveTab('photos'); }} 
            onSettingsClick={() => setActiveTab('settings')}
            onAdminClick={isAdmin ? () => setActiveTab('admin') : undefined}
            gridLayout={gridLayout}
            onGridLayoutChange={handleSetGridLayout}
          />

          {/* Quota warning banner at 80%+ */}
          {isWarning && !isBlocked && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.12)',
              borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#d97706',
              padding: '0.6rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.85rem',
              zIndex: 10
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>⚠️</span>
                <span>You've used <strong>{storagePercent}%</strong> of your {planName} storage ({storageUsedStr}). Upgrade to ensure uninterrupted uploads.</span>
              </div>
              <button 
                className="btn btn-primary"
                style={{ padding: '0.25rem 0.65rem', fontSize: '0.75rem', background: '#d97706', borderColor: '#d97706', color: '#fff' }}
                onClick={() => setCheckoutPlan({ id: 'personal', name: 'Personal', price: '$4.90' })}
              >
                Upgrade Plan
              </button>
            </div>
          )}

          {/* Blocking banner at 100% */}
          {isBlocked && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.15)',
              borderBottom: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#ef4444',
              padding: '0.75rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.875rem',
              fontWeight: 500,
              zIndex: 10
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>🚨</span>
                <span>Storage quota full ({storageUsedStr}). Uploads are blocked until you upgrade your plan or delete media.</span>
              </div>
              <button 
                className="btn btn-primary"
                style={{ padding: '0.35rem 0.85rem', fontSize: '0.8rem', background: '#ef4444', borderColor: '#ef4444' }}
                onClick={() => setCheckoutPlan({ id: 'personal', name: 'Personal', price: '$4.90' })}
              >
                Upgrade Storage
              </button>
            </div>
          )}
          
          <main className="app-content">
            <Suspense fallback={<div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading...</div>}>
              <Routes>
                {/* "/" and legacy "/#albums" links */}
                <Route index element={<Navigate to={`/${tabFromLegacyHash(location.hash, isAdmin)}`} replace />} />
                {(() => {
                  const tabCtx = {
                    tenantId,
                    gridLayout,
                    searchQuery,
                    onboardingIntent,
                    isCorporateDomain,
                  };
                  return TABS.flatMap(tab => [
                    <Route
                      key={tab.id}
                      path={tab.id}
                      element={
                        tab.adminOnly && !isAdmin
                          ? <Navigate to={`/${DEFAULT_TAB}`} replace />
                          : <tab.component {...(tab.props?.(tabCtx) ?? {})} />
                      }
                    />,
                    ...(tab.id === 'photos' ? [
                      <Route
                        key="photos-detail"
                        path="photos/:mediaId"
                        element={<tab.component {...(tab.props?.(tabCtx) ?? {})} />}
                      />
                    ] : []),
                    ...(tab.id === 'albums' ? [
                      <Route
                        key="albums-detail"
                        path="albums/:albumId"
                        element={<tab.component {...(tab.props?.(tabCtx) ?? {})} />}
                      />
                    ] : [])
                  ]);
                })()}
                <Route path="*" element={<Navigate to={`/${DEFAULT_TAB}`} replace />} />
              </Routes>
            </Suspense>
          </main>
          
          <MobileNav activeTab={activeTab} setActiveTab={setActiveTab} />
          
        </div>
        
        {showSecuritySettings && (
          <SecuritySettings onClose={() => setShowSecuritySettings(false)} />
        )}
        
        {checkoutPlan && (
          <CheckoutModal 
            tierId={checkoutPlan.id}
            tierName={checkoutPlan.name}
            price={checkoutPlan.price}
            onClose={() => setCheckoutPlan(null)}
            onSuccess={() => {
              setCheckoutPlan(null);
              refreshUsage();
              setActiveTab('settings');
            }}
          />
        )}

        <OnboardingIntentModal
          isOpen={showOnboardingModal}
          onSelectIntent={handleSelectIntent}
          onClose={() => setShowOnboardingModal(false)}
        />

        {/* Global floating toast */}
        {toast && (
          <div
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              zIndex: 10001,
              background: toast.type === 'error' ? '#ef4444' : 'var(--accent-primary)',
              color: '#ffffff',
              padding: '0.85rem 1.25rem',
              borderRadius: '10px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '0.9rem',
              maxWidth: '420px',
            }}
          >
            <span>{toast.type === 'error' ? '⚠️' : 'ℹ️'}</span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.8)',
                cursor: 'pointer',
                fontSize: '1.1rem',
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
